import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { boronClient } from "../../../api";
import { createError } from "../../../errors";
import { useUpdateStudentKartesCache } from "../../../hooks/http/useFetchStudentKartes";
import { useFlashMessage } from "../../../hooks/useFlashMessage";

const path =
  "/api/v1/students/{student_id}/ai_assistant_karte_generations/latest" as const;

export const buildAiAssistantKarteGenerationStatusQueryKey = (
  studentId: string,
) => [path, studentId];

// GEN-08: 画面表示時から10秒間隔でポーリングする
const POLLING_INTERVAL = 10_000;

export type AiAssistantKarteGenerationStatus =
  | "pending"
  | "completed"
  | "failed"
  | null;

// GEN-08: AIカルテ生成の進行状態（受付可否・最新の生成のステータス）を
// 10秒間隔でポーリングする純粋な取得フック。副作用は持たない
export const useFetchAiAssistantKarteGenerationStatus = ({
  studentId,
}: {
  studentId: string;
}) => {
  return useQuery({
    queryKey: buildAiAssistantKarteGenerationStatusQueryKey(studentId),
    queryFn: async () => {
      const { response, data } = await boronClient.GET(path, {
        params: { path: { student_id: studentId } },
      });
      if (response.ok && data) {
        return data;
      }
      throw await createError(response);
    },
    refetchInterval: POLLING_INTERVAL,
  });
};

// GEN-10/GEN-11: 最新の生成ステータスが pending → completed / failed に遷移したのを
// 検知して、フラッシュ表示とカルテ一覧のリフレッシュを行う。
// 前回ステータスはメモリ上（ref）でのみ保持する。永続化すると画面リロード時に
// 遷移を誤検知してフラッシュが再発火するため。
export const useNotifyAiAssistantKarteGenerationResult = ({
  studentId,
  status,
}: {
  studentId: string;
  // 未取得時は undefined。取得済みで生成が1件もない場合は null
  status: AiAssistantKarteGenerationStatus | undefined;
}) => {
  const { showSuccessMessage, showErrorMessage } = useFlashMessage();
  const { refetchStudentKartes } = useUpdateStudentKartesCache();

  const previousRef = useRef<{
    studentId: string;
    status: AiAssistantKarteGenerationStatus;
  } | null>(null);

  useEffect(() => {
    if (status === undefined) {
      return;
    }
    const previous = previousRef.current;
    previousRef.current = { studentId, status };
    // 生徒切り替え直後や初回取得は遷移とみなさない
    if (
      previous === null ||
      previous.studentId !== studentId ||
      previous.status !== "pending"
    ) {
      return;
    }
    if (status === "completed") {
      showSuccessMessage("カルテ生成が完了しました");
      refetchStudentKartes(studentId);
    } else if (status === "failed") {
      showErrorMessage(
        "カルテ生成に失敗しました。詳細はメールをご確認ください",
      );
    }
  }, [
    studentId,
    status,
    showSuccessMessage,
    showErrorMessage,
    refetchStudentKartes,
  ]);
};
